import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { PrefsContext } from '../contexts';
import DeferredComponent from '../ws-scripts/components/DeferredComponent';
import Promo from './Promo/Promo';
import { filterVehiclePromosByYmmt } from '../utilities/promos';
import LoadMoreButton from './LoadMoreButton';
import { aR as ASPECT_RATIO } from '../global-constants';

const Carousel = React.lazy(() => import('./Slick/Carousel'));

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.ogv', '.mov'];

const normalizeAssetUrl = (url = '') =>
	typeof url === 'string' ? url.split('|')[0].trim() : '';

const isVideoFileUrl = (url = '') =>
	VIDEO_EXTENSIONS.some((ext) =>
		normalizeAssetUrl(url).split('?')[0].toLowerCase().endsWith(ext)
	);

const promoHasVideo = (promo, aspectRatio) => {
	if (!promo) return false;

	const videoKey = `${aspectRatio}_video`;
	const assetCandidates = [
		promo?.customAssetVideoForms?.[videoKey],
		promo?.customAssetVideoForms?.[aspectRatio],
		promo?.customAssets?.[videoKey],
		promo?.customAssets?.[aspectRatio],
		promo?.customAssetVideos?.[videoKey],
		promo?.customAssetVideos?.[aspectRatio]
	];

	return assetCandidates.some((candidate) => {
		if (!candidate) return false;

		if (typeof candidate === 'object') {
			const objectVideoUrls = [
				candidate.highestUrl,
				candidate.xmlUrl,
				...(Array.isArray(candidate.sources)
					? candidate.sources.map((source) => source?.url)
					: [])
			];

			return objectVideoUrls.some((url) => url && isVideoFileUrl(url));
		}

		return isVideoFileUrl(candidate);
	});
};

const TemplateWrapper = ({
	promos,
	isMobile,
	width,
	isCoupon,
	filterParams,
	csrPlaceholder
}) => {
	const filteredPromos = filterVehiclePromosByYmmt(promos, filterParams);

	const { enableCarousel, aspectRatio, showLoadMoreButton } =
		useContext(PrefsContext);

	const isTallVerticalCoupon =
		aspectRatio === ASPECT_RATIO.DESKTOP_9X16 ||
		aspectRatio === ASPECT_RATIO.MOBILE_9X16;
	const desktopCards = isTallVerticalCoupon ? 8 : 9;
	const desktopCardsSM = 6;
	const mobileCards = 4;
	const cardsToLoad = isMobile ? mobileCards : desktopCards;

	let renderedSlideshow;
	const isCouponCaro =
		enableCarousel === 'true' &&
		isCoupon &&
		!isMobile &&
		!isTallVerticalCoupon;
	const isTallVerticalCouponCaro =
		enableCarousel === 'true' && isTallVerticalCoupon;

	// This section is a Load More button for the coupon listing placement with carousel false
	const sliceCards = (toSlice) => {
		return filteredPromos.slice(0, toSlice);
	};

	const [showMoreBtn, setShowMoreBtn] = useState(
		showLoadMoreButton === 'true' && filteredPromos.length > cardsToLoad
	);
	const [cards, loadCards] = useState(
		showMoreBtn ? sliceCards(cardsToLoad) : filteredPromos
	);

	useEffect(() => {
		if (!isMobile && showMoreBtn) {
			if (width + 48 < 992) {
				loadCards(sliceCards(desktopCardsSM));
			} else {
				loadCards(sliceCards(desktopCards));
			}
		}
		/* eslint-disable react-hooks/exhaustive-deps */
	}, [width]);

	const carouselComp = (
		<DeferredComponent placeholder={csrPlaceholder}>
			<Carousel
				isMobile={isMobile}
				promos={filteredPromos}
				width={width}
				isCouponCaro={isCouponCaro || isTallVerticalCouponCaro}
			/>
		</DeferredComponent>
	);

	// Slideshow aspect ratios that support video playback controls
	const isSlideshow =
		aspectRatio === ASPECT_RATIO.DESKTOP_4X1 ||
		aspectRatio === ASPECT_RATIO.DESKTOP_10X1 ||
		aspectRatio === ASPECT_RATIO.MOBILE_4X3_SLIDESHOW ||
		aspectRatio === ASPECT_RATIO.MOBILE_4X3_COUPON;

	if (filteredPromos.length === 1) {
		// For single video promos in slideshow aspect ratios, use Carousel
		// to show play/pause button for user control
		const singlePromoHasVideo =
			isSlideshow && promoHasVideo(filteredPromos[0], aspectRatio);

		if (singlePromoHasVideo) {
			renderedSlideshow = carouselComp;
		} else {
			// Display single image without carousel
			const promo = (
				<Promo
					isMobile={isMobile}
					isVisible
					promo={filteredPromos[0]}
				/>
			);

			// Promo may not render (e.g. an incentive promo with no matching incentive)
			if (promo) {
				renderedSlideshow = (
					// Wrap with markup similar to what's added by react-slick
					<div
						className="single-promo-container"
						data-testid="single-container"
					>
						<div className="single-promo">
							<div>{promo}</div>
						</div>
					</div>
				);
			}
		}
	} else if (isCoupon) {
		// For Desktop - render slick when pref is enabled and there's more than 2 promos (screen dragging)
		// For Mobile - render slick when pref is enabled for tall vertical coupons
		// For Tall Vertical Coupons - render slick when pref is enabled and there's more than 2 promos (same as 4x3)
		if (
			(isCouponCaro && filteredPromos.length > 2) ||
			(isTallVerticalCouponCaro &&
				!isMobile &&
				filteredPromos.length > 2) ||
			(isTallVerticalCouponCaro &&
				isMobile &&
				filteredPromos.length > 1) ||
			(enableCarousel === 'true' &&
				isMobile &&
				!isTallVerticalCoupon &&
				filteredPromos.length > 1)
		) {
			renderedSlideshow = carouselComp;
		} else {
			renderedSlideshow = (
				// Wrap with markup similar to what's added by react-slick
				// this is grid
				<div className="coupon-container d-flex flex-wrap">
					{cards?.map((promo) => (
						<Promo
							isMobile={isMobile}
							isVisible
							promo={promo}
							key={promo.id}
						/>
					))}
					<LoadMoreButton
						onClick={() => {
							loadCards(filteredPromos);
							setShowMoreBtn(false);
						}}
						showMoreBtn={showMoreBtn}
					/>
				</div>
			);
		}
	} else {
		renderedSlideshow = carouselComp;
	}

	return renderedSlideshow;
};

TemplateWrapper.propTypes = {
	csrPlaceholder: PropTypes.node,
	promos: PropTypes.arrayOf(PropTypes.shape({})),
	isMobile: PropTypes.bool.isRequired,
	width: PropTypes.number.isRequired,
	filterParams: PropTypes.shape({})
};

export default TemplateWrapper;
